import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, DownloadSimple, Copy, Warning, Key } from "@phosphor-icons/react";

/**
 * Superadmin 2FA challenge — shown right after /auth/login returns
 * ``requires_totp: true``. Two modes:
 *  - `has_totp === false` → enrolment (calls /auth/totp/setup, shows QR)
 *  - `has_totp === true`  → challenge (6-digit code only)
 * On success, calls onSuccess(user) so the parent (Login page) can redirect.
 */
export default function TotpChallenge({ email, hasTotp, onSuccess, onCancel }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null); // {secret, qr_data_url}
  const [loadingSetup, setLoadingSetup] = useState(!hasTotp);
  const inputRef = useRef(null);
  // Backup codes shown ONCE after first successful activation
  const [freshBackupCodes, setFreshBackupCodes] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  // Guard against React.StrictMode running useEffect twice in dev — without
  // this, /auth/totp/setup gets called twice and the second call overwrites
  // the pending secret, invalidating the QR that was just scanned.
  const setupCalled = useRef(false);

  useEffect(() => {
    if (hasTotp) return;
    if (setupCalled.current) return;
    setupCalled.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/totp/setup");
        setSetup(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossible de démarrer la configuration 2FA");
        onCancel?.();
      } finally { setLoadingSetup(false); }
    })();
  }, [hasTotp, onCancel]);

  useEffect(() => { inputRef.current?.focus(); }, [loadingSetup]);

  const submit = async (e) => {
    e?.preventDefault();
    if (code.replace(/\s/g, "").length < 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/verify", { code: code.replace(/\s/g, "") });
      // First-time activation: server returns backup_codes ONCE — display
      // them fullscreen before letting the user proceed.
      if (data.backup_codes && data.backup_codes.length > 0) {
        setFreshBackupCodes(data.backup_codes);
        setPendingUser(data.user);
        return;
      }
      toast.success("Authentification 2FA réussie");
      onSuccess(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code incorrect");
      setCode("");
      inputRef.current?.focus();
    } finally { setBusy(false); }
  };

  if (freshBackupCodes) {
    return <BackupCodesReveal
      email={email}
      codes={freshBackupCodes}
      onDone={() => onSuccess(pendingUser)}
    />;
  }

  return (
    <div className="min-h-screen bg-[#0B1B2E] flex items-center justify-center p-6" data-testid="totp-challenge">
      <div className="w-full max-w-md bg-[#0F2136] border border-white/10 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#E4572E]/20 flex items-center justify-center">
            <ShieldCheck size={22} weight="fill" className="text-[#E4572E]" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Authentification à deux facteurs</h1>
            <p className="text-xs text-[#8494A8] mt-0.5 break-all">{email}</p>
          </div>
        </div>

        {!hasTotp && (
          <div className="mb-6">
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-4">
              <p className="text-amber-300 font-semibold text-sm mb-1">Configuration initiale</p>
              <p className="text-xs text-[#B7C4D6]">
                Votre compte est administrateur plateforme — le 2FA est obligatoire. Scannez le QR code avec Google Authenticator, Microsoft Authenticator, 1Password ou Authy.
              </p>
            </div>
            {loadingSetup ? (
              <p className="text-sm text-[#8494A8]">Génération du secret…</p>
            ) : setup ? (
              <div className="text-center">
                <img src={setup.qr_data_url} alt="QR code 2FA" className="mx-auto rounded-xl bg-white p-3" width={192} height={192} data-testid="totp-qr" />
                <p className="text-xs text-[#8494A8] mt-3">Ou entrez manuellement ce code :</p>
                <code className="text-[#B0C97F] text-xs font-mono tracking-wider break-all block mt-1" data-testid="totp-secret">{setup.secret}</code>
              </div>
            ) : null}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8494A8] mb-2 block">
              {hasTotp ? "Code à 6 chiffres de votre application" : "Confirmez avec le code affiché sur l'application"}
            </label>
            <input
              ref={inputRef}
              type="text" inputMode="numeric" pattern="[0-9 ]*" autoComplete="one-time-code"
              value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, "").slice(0, 8))}
              placeholder="123 456"
              className="w-full h-14 bg-[#081221] border border-white/10 rounded-xl text-white text-center text-3xl font-mono tracking-[0.4em] focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/30"
              data-testid="totp-code"
            />
          </div>
          <Button
            type="submit" disabled={busy || code.replace(/\s/g, "").length < 6}
            className="w-full h-12 bg-[#E4572E] hover:bg-[#F27551] text-white font-semibold rounded-xl"
            data-testid="totp-submit"
          >
            {busy ? "Vérification…" : (hasTotp ? "Vérifier" : "Activer le 2FA")}
          </Button>
          <button
            type="button" onClick={onCancel}
            className="w-full text-center text-sm text-[#8494A8] hover:text-white transition"
            data-testid="totp-cancel"
          >
            Annuler et revenir à la connexion
          </button>
        </form>

        <p className="text-[10px] text-[#5F758F] mt-6 text-center leading-relaxed">
          Le 2FA est requis pour tout accès au backoffice plateforme, conformément à ISO 27001 / RGPD Art. 32.
          {hasTotp && <><br />Perdu votre téléphone ? Entrez un code de secours à la place (format <code className="text-[#B0C97F]">xxxx-xxxx</code>).</>}
        </p>
      </div>
    </div>
  );
}

/**
 * Backup-code reveal screen — shown ONCE right after 2FA activation.
 * User cannot proceed without acknowledging the download/copy.
 */
function BackupCodesReveal({ email, codes, onDone }) {
  const [acknowledged, setAcknowledged] = useState(false);

  const download = () => {
    const content =
      `Codes de secours 2FA — Questiomed Backoffice\n` +
      `Compte : ${email}\n` +
      `Généré le : ${new Date().toLocaleString("fr-FR")}\n\n` +
      `IMPORTANT : Chaque code est à usage unique.\n` +
      `Stockez ce fichier dans un coffre-fort (1Password, Bitwarden, papier scellé, ...).\n\n` +
      codes.map((c, i) => `  ${String(i + 1).padStart(2, "0")}.  ${c}`).join("\n") +
      `\n\nSi vous en avez utilisé plusieurs, régénérez-en depuis /superadmin (Sécurité).`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `questiomed-backup-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setAcknowledged(true);
    toast.success("Codes téléchargés — mettez-les dans votre coffre-fort");
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setAcknowledged(true);
      toast.success("Codes copiés dans le presse-papiers");
    } catch { toast.error("Copie impossible — utilisez le téléchargement"); }
  };

  return (
    <div className="min-h-screen bg-[#0B1B2E] flex items-center justify-center p-6" data-testid="backup-codes-reveal">
      <div className="w-full max-w-lg bg-[#0F2136] border border-white/10 rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-[#E4572E]/20 flex items-center justify-center">
            <Key size={22} weight="fill" className="text-[#E4572E]" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Codes de secours 2FA</h1>
            <p className="text-xs text-[#8494A8] mt-0.5">Sauvegardez-les MAINTENANT</p>
          </div>
        </div>

        <div className="p-4 bg-red-500/10 border border-red-500/40 rounded-xl mb-5 flex items-start gap-3">
          <Warning size={20} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-[#F0BAB4] leading-relaxed">
            <p className="font-semibold text-red-300 mb-1">Cette page ne sera plus jamais affichée.</p>
            En cas de perte de votre téléphone, chaque code ci-dessous vous permet de vous connecter une fois. Stockez-les dans votre gestionnaire de mots de passe ou imprimez-les.
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5 font-mono text-sm bg-[#081221] rounded-xl p-4 border border-white/10" data-testid="backup-codes-list">
          {codes.map((c, i) => (
            <div key={c} className="flex items-center gap-2 py-1.5">
              <span className="text-[#5F758F] text-xs w-5">{String(i + 1).padStart(2, "0")}.</span>
              <code className="text-[#B0C97F] tracking-wider">{c}</code>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-4">
          <Button onClick={download} className="flex-1 bg-[#E4572E] hover:bg-[#F27551] text-white" data-testid="backup-codes-download">
            <DownloadSimple size={16} className="mr-2" /> Télécharger .txt
          </Button>
          <Button onClick={copyAll} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10" data-testid="backup-codes-copy">
            <Copy size={16} className="mr-2" /> Copier
          </Button>
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-5 p-3 bg-[#081221] rounded-xl border border-white/10">
          <input
            type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
            className="w-4 h-4 accent-[#E4572E] mt-0.5"
            data-testid="backup-codes-ack"
          />
          <span className="text-sm text-[#B7C4D6]">
            J'ai sauvegardé ces codes dans un endroit sûr. Je comprends qu'ils sont uniques et ne seront pas ré-affichés.
          </span>
        </label>

        <Button
          onClick={onDone} disabled={!acknowledged}
          className="w-full h-12 bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold rounded-xl disabled:opacity-40"
          data-testid="backup-codes-continue"
        >
          Continuer vers le backoffice
        </Button>
      </div>
    </div>
  );
}
